import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Mail, Copy, Check } from 'lucide-react';
import { antiScrapingOptimized } from '../../utils/antiScrapingOptimized';

interface ProtectedEmailProps {
  email: string;
  displayName?: string;
  className?: string;
}

const EmailContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  position: relative;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &::selection {
    background: transparent;
  }
  &::-moz-selection {
    background: transparent;
  }
`;

const EmailIcon = styled(Mail)`
  color: ${props => props.theme.colors.primary[600]};
  flex-shrink: 0;
`;

const EmailText = styled.span`
  font-family: ${props => props.theme.typography.fonts.mono};
  font-size: ${props => props.theme.typography.sizes.sm};
  color: ${props => props.theme.colors.text};
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background-color: ${props => props.$copied ? props.theme.colors.success : props.theme.colors.neutral[200]};
  color: ${props => props.$copied ? 'white' : props.theme.colors.neutral[700]};
  border-radius: ${props => props.theme.borderRadius.full};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  flex-shrink: 0;

  &:hover {
    background-color: ${props => props.$copied ? props.theme.colors.success : props.theme.colors.neutral[300]};
  }
`;

const Tooltip = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colors.neutral[800]};
  color: white;
  padding: ${props => props.theme.spacing[1]} ${props => props.theme.spacing[2]};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.xs};
  white-space: nowrap;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.smooth};
  z-index: 1000;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 4px solid ${props => props.theme.colors.neutral[800]};
  }
`;

const ProtectedEmail: React.FC<ProtectedEmailProps> = ({
  email,
  displayName,
  className
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [obfuscatedEmail, setObfuscatedEmail] = useState('');

  useEffect(() => {
    // Obfusquer l'email
    const obfuscated = antiScrapingOptimized.obfuscate(email);
    setObfuscatedEmail(obfuscated);

    // Vérifier si c'est un bot
    const botDetection = antiScrapingOptimized.detectBot();
    
    if (botDetection.isBot) {
      console.warn('🚫 Bot detected, email protected');
      return;
    }

    // Délai minimal pour l'affichage
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => clearTimeout(timer);
  }, [email]);

  const handleClick = async () => {
    if (!isVisible) return;

    try {
      const deobfuscatedEmail = antiScrapingOptimized.deobfuscate(obfuscatedEmail);
      
      // Copier dans le presse-papier
      await navigator.clipboard.writeText(deobfuscatedEmail);
      
      setCopied(true);
      setShowTooltip(true);
      
      setTimeout(() => {
        setCopied(false);
        setShowTooltip(false);
      }, 2000);
      
      antiScrapingOptimized.showWarning('✅ Email copié !');
    } catch (error) {
      console.error('Erreur lors de la copie:', error);
      antiScrapingOptimized.showWarning('❌ Erreur lors de la copie');
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    antiScrapingOptimized.showWarning('⚠️ Clic droit désactivé');
  };

  if (!isVisible) {
    return (
      <EmailContainer className={className} style={{ opacity: 0.3 }}>
        <EmailText>Chargement de l'email...</EmailText>
      </EmailContainer>
    );
  }

  return (
    <EmailContainer 
      className={className}
      onContextMenu={handleContextMenu}
    >
      <EmailIcon size={16} />
      <EmailText>{displayName || antiScrapingOptimized.deobfuscate(obfuscatedEmail)}</EmailText>
      <CopyButton 
        onClick={handleClick} 
        onMouseEnter={() => setShowTooltip(true)} 
        onMouseLeave={() => setShowTooltip(false)}
        $copied={copied}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        <Tooltip $visible={showTooltip}>
          {copied ? 'Copié !' : 'Copier l\'email'}
        </Tooltip>
      </CopyButton>
    </EmailContainer>
  );
};

export default ProtectedEmail;